import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import styles from './index.module.scss'
import Header from '../../components/Header'
import WorkspaceService from '../../services/workspace.service'
import Modal from '../../components/Modal'
import EditIcon from '../../assets/icon/EditIcon'
import TrashIcon from '../../assets/icon/TrashIcon'
import { HOME_PAGE_URI_CONST } from '../../constants/api.constant'
import {useWorkspace, Workspace} from "../../context/workspace.context";

export default function Workspaces() {
    const navigate = useNavigate()
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editName, setEditName] = useState('')
    const [deleteModal, setDeleteModal] = useState<Workspace | null>(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const {workspaces, isWorkspaceLoading, fetchWorkspaces} = useWorkspace()

    const handleEdit = (ws: Workspace) => {
        setEditingId(ws.id)
        setEditName(ws.name)
    }

    const handleSaveEdit = async (ws: Workspace) => {
        if (!editName.trim()) return
        try {
            await WorkspaceService.updateWorkspace(ws.id, {
                name: editName.trim(),
                is_public: ws.is_public,
            })

            await fetchWorkspaces()
        } catch (e) {
            console.error('Failed to update workspace:', e)
        } finally {
            setEditingId(null)
        }
    }

    const handleCancelEdit = () => {
        setEditingId(null)
        setEditName('')
    }

    const handleDelete = async () => {
        if (!deleteModal) return
        setDeleteLoading(true)
        try {
            await WorkspaceService.deleteWorkspace(deleteModal.id)
            await fetchWorkspaces()

            setDeleteModal(null)
        } catch (e) {
            console.error('Failed to delete workspace:', e)
        } finally {
            setDeleteLoading(false)
        }
    }

    const getInitial = (name: string) => {
        return name.charAt(0).toUpperCase()
    }

    const handleParticipantsNavigate = (workspaceId: number) => {
        navigate(`/participants/${workspaceId}`)
    }

    return (
        <>
            <Header />
            <div className={styles.workspaces}>
                <div className={styles.workspaces__topBar}>
                    <div className={styles.workspaces__back} onClick={() => navigate(HOME_PAGE_URI_CONST)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.75 17L5.75 12M5.75 12L10.75 7M5.75 12L18.25 12" stroke="#64617E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className={styles.workspaces__backTitle}>Настройки пространства</span>
                    </div>
                </div>

                {isWorkspaceLoading ? (
                    <div className={styles.workspaces__loading}>Загрузка...</div>
                ) : workspaces.length === 0 ? (
                    <div className={styles.workspaces__empty}>Нет рабочих пространств</div>
                ) : (
                    <div className={styles.workspaces__list}>
                        {workspaces.map(ws => (
                            <div className={styles.workspaces__item} key={ws.id}>
                                <div className={styles.workspaces__itemHeader}>
                                    <div className={styles.workspaces__itemIcon}>
                                        {getInitial(ws.name)}
                                    </div>
                                    {editingId === ws.id ? (
                                        <div className={styles.workspaces__itemEditRow}>
                                            <input
                                                className={styles.workspaces__itemEditInput}
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveEdit(ws)
                                                    if (e.key === 'Escape') handleCancelEdit()
                                                }}
                                                autoFocus
                                            />
                                            <button className={styles.workspaces__itemSaveBtn} onClick={() => handleSaveEdit(ws)}>
                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15 5L7 13L3 9" stroke="#12B76A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            <button className={styles.workspaces__itemCancelBtn} onClick={handleCancelEdit}>
                                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M13.5 4.5L4.5 13.5" stroke="#F04438" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M4.5 4.5L13.5 13.5" stroke="#F04438" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className={styles.workspaces__itemName}
                                                onClick={() => handleParticipantsNavigate(ws.id)}
                                            >
                                                {ws.name}
                                            </button>
                                            <button className={styles.workspaces__itemEditBtn} onClick={() => handleEdit(ws)}>
                                                <EditIcon />
                                            </button>
                                        </>
                                    )}
                                </div>
                                <button className={styles.workspaces__deleteBtn} onClick={() => setDeleteModal(ws)}>
                                    <TrashIcon />
                                    <span>Удалить рабочее пространство</span>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <Modal isOpen={!!deleteModal} onRequestClose={() => setDeleteModal(null)} width={400}>
                    <div className={styles.workspaces__deleteModal}>
                        <h3 className={styles.workspaces__deleteModalTitle}>Удалить пространство</h3>
                        <p className={styles.workspaces__deleteModalText}>
                            Вы уверены, что хотите удалить пространство "{deleteModal?.name}"? Это действие нельзя отменить.
                        </p>
                        <div className={styles.workspaces__deleteModalActions}>
                            <button className={styles.workspaces__deleteModalCancel} onClick={() => setDeleteModal(null)}>
                                Отмена
                            </button>
                            <button
                                className={styles.workspaces__deleteModalConfirm}
                                onClick={handleDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Удаление...' : 'Удалить'}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </>
    )
}
